import React, { useEffect, useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { PrimaryButton, ErrorButton, SecondaryButton, AgTableCard, Input } from "../UI";
import { useUserContext } from '../../contexts/UserContext';
import { getProducts, createProduct, updateProduct, deleteProduct } from '../../utils/APIs';
import ProductModal from './ProductModal';
import FunctionModal from './FunctionModal';
import Loader from '../UI/Loader/Loader';
import SuccessAlert from '../UI/Alert/SuccessAlert';
import PrimaryModal from '../UI/Modal/PrimaryModal';
import { useContentfulContext } from '../../contexts/ContentfulContext';

const Products = () => {
  const { content } = useContentfulContext();

  const [gridApi, setGridApi] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [success, setSuccess] = useState(null);
  const [functionModal, setFunctionModal] = useState({ open: false, functions: null });
  const [productModal, setProductModal] = useState({ action: "", data: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const { userData } = useUserContext();

  const getAllProducts = async () => {
    try {
      setTableLoading(true);
      const res = await getProducts(userData?.token);
      if(res.status){
        setRowData(res.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setTableLoading(false);
    }
  };

  const saveProduct = async (data) => {
    try {
      setLoading(true);
      const res = await createProduct(userData?.token, data);
      if(res.status){
        setSuccess(res.message);
        getAllProducts();
        hideProductModal();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const editProduct = async (id, data) => {
    try {
      setLoading(true);
      const res = await updateProduct(userData?.token, id, data);
      if(res.status){
        setSuccess(res.message);
        getAllProducts();
        hideProductModal(); 
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      setLoading(true);
      const res = await deleteProduct(userData?.token, productId);
      if(res.status){
        setSuccess(res.message);
        getAllProducts();
        hideDeleteModal(); 
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllProducts();
  }, []);

  const colDefs = [
    {
      headerName: content.MANAGE_PRODUCTS.table_headers.product_name,
      field: "name",
      filter: true,
      cellRenderer: ({ data }) => (
        <div className="relative inline-block text-left">
          <button className='text-center block cursor-pointer text-primary'
            onClick={() => setProductModal({ action: 'update', data: data })}
          >
            {data.name}
          </button>
        </div>
      ),
      suppressMovable: true
    },
    {
      headerName: content.MANAGE_PRODUCTS.table_headers.product_id,
      field: "id",
      filter: true,
      sortable: true,
      suppressMovable: true
    },
    {
      headerName: content.MANAGE_PRODUCTS.table_headers.scope,
      field: "scope",
      cellRenderer: ({ data }) => (data.scope ? data.scope : "N/A"),
      filter: true,
      sortable: true,
      suppressMovable: true
    },
    {
      headerName: content.MANAGE_PRODUCTS.table_headers.functions,
      field: "functions",
      cellRenderer: ({ data }) => {
        return <div className="relative inline-block text-left">
          <button
            className='text-center block cursor-pointer text-primary'
            onClick={() => setFunctionModal({ open: true, functions: data.functions })}
          >
            View details
          </button>
        </div>
      },
      filter: true,
      sortable: true,
      suppressMovable: true
    },
    {
      headerName : content.MANAGE_PRODUCTS.table_headers.action,
      cellRenderer: ({ data }) => {
        return <div className="relative inline-block text-left">
          <button
            className='text-center block cursor-pointer text-primary'
            onClick={() => setDeleteModal({ open: true, id: data._id })}
          >
            Delete
          </button>
        </div>
      },
      suppressMovable: true
    }
  ];

  const onFilterTextBoxChanged = (event) => {
    const filterValue = event.target.value.toLowerCase();
    gridApi.setFilterModel({
      name: { type: 'contains', filter: filterValue }
    });
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const autoSizeStrategy = useMemo(() => ({
    type: "fitGridWidth",
    defaultMinWidth: 100,
    columnLimits: [
      { colId: "name", minWidth: 200 },
      { colId: "id", minWidth: 150 },
      { colId: "scope", minWidth: 300 },
      { colId: "functions", minWidth: 150 }
    ],
  }), []);

  const handleProductSubmit = (product) => {
    if (productModal.action === "create") {
      saveProduct(product);
    } else {
      const payload = {...product}
      delete payload.id
      editProduct(product.id, payload);
    }
  };
  
  const hideProductModal = () => {
    setProductModal({ action: "", data: null })
  }

  const hideDeleteModal = () => {
    setDeleteModal({ open: false, data: null })
  }

  return (
    <>
      <main id="main" className="flex flex-col w-full space-y-4 my-6 px-4 md:px-8">
        {loading && <Loader />}
        {success && (
          <SuccessAlert disappearTime={content.MANAGE_PRODUCTS.timeout}>
            {success}
          </SuccessAlert>
        )}
        <div className='flex justify-between'>
          <h1 className="text-xl font-medium leading-tight tracking-tight text-gray-900 md:text-2xl">
            {content.MANAGE_PRODUCTS.title}
          </h1>
        </div>

        <AgTableCard
          header={
            <>
              <div className="w-full sm:w-80">
                <Input
                  placeholder={content.MANAGE_PRODUCTS.search_placeholder}
                  onChange={onFilterTextBoxChanged}
                  prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                  clearable
                />
              </div>
              <div>
                <PrimaryButton className='!font-bold' handleClick={() => setProductModal({ action: "create", data: null })}>
                  {content.MANAGE_PRODUCTS.cta_text.create_function}
                </PrimaryButton>
              </div>
            </>
          }
          tableId="manage-products-table"
          loading={tableLoading}
          rowData={rowData}
          columnDefs={colDefs}
          autoSizeStrategy={autoSizeStrategy}
          onGridReady={onGridReady}
        />
      </main>

      {/* Delete Product Modal */}
      {deleteModal.open && (
        <PrimaryModal title={content.MANAGE_PRODUCTS.delete.title} hideModal={() => {
          hideDeleteModal();
        }}>
          <div className='p-6 space-y-6'>
            <div class="flex flex-col space-y-2 mb-6">
              <p className='mb-4'>{content.MANAGE_PRODUCTS.delete.description}</p>
              <div class="mb-4 space-x-4">
                <ErrorButton handleClick={() => {
                  handleDeleteProduct(deleteModal.id);
                }}>{content.MANAGE_PRODUCTS.delete.delete_cta_text}</ErrorButton>
                <SecondaryButton handleClick={() => {
                  hideDeleteModal()
                }}>{content.MANAGE_PRODUCTS.delete.cancel_cta_text}</SecondaryButton>
              </div>
            </div>
          </div>
        </PrimaryModal>
      )}

      {/* Create/Edit Product Modal */}
      {productModal.action && (
        <ProductModal
          action={productModal.action}
          data={productModal.data}
          hideModal={hideProductModal}
          handleSubmit={handleProductSubmit}
        />
      )}

      {/* View Functions Modal */}
      {functionModal.open && (
        <FunctionModal
          functions={functionModal.functions}
          hideModal={() => setFunctionModal({ open: false, functions: null })}
        />
      )}
    </>
  );
};

export default Products;
